import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";
import { CommandItem } from "../../Command";
import { SelectContext } from "./context";

function getChildrenText(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getChildrenText).join("").trim();
  if (React.isValidElement(node)) {
    return getChildrenText(
      (node.props as { children?: React.ReactNode }).children,
    );
  }
  return "";
}

export function SelectItem({
  className,
  children,
  textValue,
  value,
  disabled,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>) {
  const ctx = React.useContext(SelectContext);
  const {
    registerItem,
    selectedValueSet,
    handleItemSelect,
    multiple,
    isMobile,
  } = ctx;
  const itemText = textValue ?? (getChildrenText(children) || value);
  React.useEffect(() => {
    registerItem(value, itemText);
  }, [registerItem, value, itemText]);

  // -------------------------------------------------------------------------
  // Simple Radix branch
  // -------------------------------------------------------------------------
  if (ctx.isSimple) {
    return (
      <SelectPrimitive.Item
        value={value}
        disabled={disabled}
        textValue={textValue}
        className={cn(
          "relative flex w-full cursor-default select-none items-center rounded-xs py-3 pl-8 pr-2 text-base outline-hidden focus:bg-gray-100 focus:text-black data-disabled:pointer-events-none data-disabled:opacity-50 md:py-1.5 md:text-sm",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.ItemIndicator>
          <span className="absolute left-2 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center">
            <Check className="size-4" />
          </span>
        </SelectPrimitive.ItemIndicator>
        <SelectPrimitive.ItemText asChild>
          <div className="w-full">{children}</div>
        </SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    );
  }

  // -------------------------------------------------------------------------
  // Enhanced branch — CommandItem with label registry
  // -------------------------------------------------------------------------

  const isSelected = selectedValueSet.has(value);
  const cmdValue = `${itemText} ${value}`;

  return (
    <CommandItem
      value={cmdValue}
      disabled={disabled}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-xs py-2 pl-8 pr-2 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        multiple && "py-3 md:py-2",
        isMobile &&
          "py-3 text-base md:py-2 md:text-sm data-[selected=true]:bg-transparent data-[selected=true]:text-black",
        isMobile && isSelected && "font-semibold",
        className,
      )}
      onSelect={() => handleItemSelect(value, itemText)}
    >
      {isSelected ? (
        <span className="absolute left-2 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center">
          <Check className="size-4" />
        </span>
      ) : null}
      <div className="w-full">{children}</div>
    </CommandItem>
  );
}

SelectItem.displayName = SelectPrimitive.Item.displayName;
